'use client';

import { m, type Variants } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import GlobalPartnersCount from './GlobalPartnersCount';
import InterCustomerSlider from './InterCustomerSlider';

/**
 * The "Trusted By N+ Global Partners" band — heading plus the partner logo
 * marquee with its edge fades.
 *
 * This markup was duplicated across a dozen marketing pages, each with its own
 * slightly different heading tag, colour, weight and casing. It now lives here
 * once; pages pass only the spacing that is genuinely page-specific.
 */

const fadeIn: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

interface TrustedPartnersProps {
  /** Outer section spacing. Defaults to the padding most pages already used. */
  className?: string;
  /** Rendered as <h2> by default; pass "p" where the page already has its heading structure. */
  as?: 'h2' | 'p';
  /** Override the heading typography if a page needs it. */
  headingClassName?: string;
  /**
   * Colour the edge fades blend into. Defaults to white; pass the page's own
   * background where the band sits on a tinted surface.
   */
  maskColor?: string;
}

const TrustedPartners = ({
  className = 'relative pt-[32px] md:pt-[60px]',
  as = 'h2',
  headingClassName = 'relative z-50 text-center font-Schibsted_Grotesk text-[16px] md:text-[20px] font-medium leading-[120%] text-[#090D28]',
  maskColor = '#FFF',
}: TrustedPartnersProps) => {
  const [partnersRef, partnersInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const Heading = as === 'p' ? m.p : m.h2;

  return (
    <section ref={partnersRef} className={className}>
      <Heading
        variants={fadeIn}
        initial="hidden"
        animate={partnersInView ? 'visible' : 'hidden'}
        transition={{ delay: 0.2 }}
        className={headingClassName}
      >
        Trusted By <GlobalPartnersCount />+ Global Partners
      </Heading>

      <div className="relative overflow-hidden">
        <InterCustomerSlider />
        <div
          className="pointer-events-none absolute right-0 top-0 z-20 h-full w-[80px] flex-shrink-0 md:w-[280px]"
          style={{ background: `linear-gradient(270deg, ${maskColor} 30%, rgba(255,255,255,0) 100%)` }}
        />
        <div
          className="pointer-events-none absolute left-0 top-0 z-20 h-full w-[80px] flex-shrink-0 md:w-[280px]"
          style={{ background: `linear-gradient(90deg, ${maskColor} 30%, rgba(255,255,255,0) 100%)` }}
        />
      </div>
    </section>
  );
};

export default TrustedPartners;
